import { isAppMode } from './appMode';

export const BUILD_VERSION_URL = `${process.env.PUBLIC_URL || ''}/build-version.json`;

export interface BuildVersionInfo {
    buildId: string;
    builtAt?: string;
}

/** Parse the hashed main bundle id from the currently loaded script tags. */
export function getLoadedBuildId(): string | null {
    if (typeof document === 'undefined') return null;

    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
        const src = scripts[i].src;
        if (!src) continue;
        const match = src.match(/\/static\/js\/main\.([a-f0-9]+)\.js/i);
        if (match) return match[1];
    }
    return null;
}

export async function fetchDeployedBuildVersion(): Promise<BuildVersionInfo | null> {
    try {
        const url = `${BUILD_VERSION_URL}?t=${Date.now()}`;
        const response = await fetch(url, {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
            },
        });
        if (!response.ok) return null;
        const data = (await response.json()) as BuildVersionInfo;
        if (!data?.buildId) return null;
        return data;
    } catch (err) {
        console.warn('[TRWidget] fetchDeployedBuildVersion failed', err);
        return null;
    }
}

export async function checkForAppUpdate(): Promise<{
    updateAvailable: boolean;
    loadedBuildId: string | null;
    remoteBuildId: string | null;
}> {
    const loadedBuildId = getLoadedBuildId();
    const remote = await fetchDeployedBuildVersion();
    const remoteBuildId = remote?.buildId ?? null;

    if (!loadedBuildId || !remoteBuildId) {
        return { updateAvailable: false, loadedBuildId, remoteBuildId };
    }

    return {
        updateAvailable: loadedBuildId !== remoteBuildId,
        loadedBuildId,
        remoteBuildId,
    };
}

const DISMISS_KEY = 'trwidget:dismissedBuildId';

export function dismissUpdateForBuild(remoteBuildId: string) {
    try {
        sessionStorage.setItem(DISMISS_KEY, remoteBuildId);
    } catch {
        /* ignore */
    }
}

export function isUpdateDismissedForBuild(remoteBuildId: string): boolean {
    try {
        return sessionStorage.getItem(DISMISS_KEY) === remoteBuildId;
    } catch {
        return false;
    }
}

/** WebView / official app: reload when user returns if a new build is deployed. */
export function shouldAutoReloadInAppMode(remoteBuildId: string): boolean {
    return isAppMode() && !isUpdateDismissedForBuild(remoteBuildId);
}

export function reloadApp() {
    window.location.reload();
}
